// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { productImageCarouselStyles } from "@conklin/Overrides/Components/ProductImageCarousel";
import { color, xScreen } from "@conklin/XDL/Utilities";
import { css } from "styled-components";

const fadeOutStyles = css`
    content: " ";
    height: 24px;
    width: 64px;
    position: absolute;
    z-index: -1;
    top: 8px;
    right: -20px;
    ${xScreen("lte", "md")} {
        height: 64px;
        width: 24px;
        top: -20px;
    }
`;

const getImageCss = () => css`
    opacity: 0.3;
    width: 100%;
    height: 100%;
    height: 64px;
    width: 64px;
    cursor: pointer;
    justify-content: flex-start;

    img {
        height: 64px;
        width: 64px;
    }
    border: 1px solid ${color("common.ui5")};
    ${xScreen("lte", "md")} {
        &,
        img {
            @media print {
                max-width: none;
                max-height: none;
                width: 115px;
                height: 115px;
                padding: 1px;
            }
        }
    }
`;

productImageCarouselStyles.mainContainer = {
    gap: 0,
    offsetCss: css`
        position: relative;
    `,
    css: css`
        ${({ isVertical }: any) => {
            if (isVertical) {
                return css`
                    flex-grow: 0;
                    width: auto;
                `;
            }
        }}
    `,
};

productImageCarouselStyles.carouselSlideInner = {
    css: css`
        width: 100%;
        text-align: center;
        position: relative;
        padding: 0;
        display: flex;
        justify-content: center;

        button {
            display: flex !important;
        }
    `,
};

productImageCarouselStyles.image = {
    css: getImageCss(),
};
productImageCarouselStyles.selectedImage = {
    css: css`
        ${getImageCss()};
        opacity: 1;
        border: 1px solid ${color("common.ui5")};
        max-height: 64px;
        width: 64px;
        height: 64px;
        ${xScreen("lte", "md")} {
            @media print {
                max-height: none;
            }
        }
    `,
};

productImageCarouselStyles.slidesContainer = {
    css: css`
        @media print {
            display: flex !important;
            flex-direction: row;
            height: 115px;
        }
        ${(props: any) => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * (64 + 8)}px;` : "")}
    `,
};

productImageCarouselStyles.prevArrowButton = productImageCarouselStyles.nextArrowButton = {
    variant: "secondary",
    color: "common.black",
    css: css`
        border: 0;
        padding: 0;
        &&:disabled {
            background: none;
        }

        &:hover svg {
            color: ${color("common.ui10")};
        }
    `,
};

productImageCarouselStyles.verticalPrevArrowGridItem = {
    width: 12,
    align: "middle",
    css: css`
        justify-content: center;
        &&& {
            &:empty {
                margin-bottom: 0;
            }
            position: absolute;
            top: -8px;
            left: calc(50% - 12px);
            z-index: 2;
        }
    `,
};
productImageCarouselStyles.verticalNextArrowGridItem = {
    width: 12,
    align: "middle",

    css: css`
        position: absolute;
        bottom: -8px;
        left: calc(50% - 12px);
        z-index: 2;
    `,
};

productImageCarouselStyles.prevArrowGridItem = {
    width: 1,
    align: "middle",
    css: css`
        justify-content: flex-end;
        &:empty {
            display: none;
        }
        position: absolute;
        right: 12px;
        top: calc(50% - 12px);
        z-index: 2;
        ${xScreen("lte", "md")} {
            left: 12px;
            right: unset;
        }
        ${xScreen("lte", "sm")} {
            left: 0;
        }
    `,
};
productImageCarouselStyles.nextArrowGridItem = {
    width: 1,
    align: "middle",
    css: css`
        justify-content: flex-end;
        &:empty {
            display: none;
        }
        position: absolute;
        left: 12px;
        top: calc(50% - 12px);
        z-index: 2;
        ${xScreen("lte", "md")} {
            right: 12px;
            left: unset;
        }
        ${xScreen("lte", "sm")} {
            right: 0;
        }
    `,
};

productImageCarouselStyles.carouselGridItem = {
    width: 12,
};

productImageCarouselStyles.verticalCarouselGridItem = {
    width: 12,
    css: css`
        max-height: 346px;
    `,
};

productImageCarouselStyles.prevFadeOut = {
    css: css`
        &::after {
            ${fadeOutStyles}
            background: linear-gradient(0, ${color("common.white", 0)} 0%, ${color("common.white")} 100%);
            ${xScreen("lte", "md")} {
                background: linear-gradient(270deg, ${color("common.white", 0)} 0%, ${color("common.white")} 100%);
                left: 0;
                right: unset;
            }
        }
    `,
};

productImageCarouselStyles.nextFadeOut = {
    css: css`
        &::after {
            ${fadeOutStyles}
            background: linear-gradient(180deg, ${color("common.white", 0)} 0%, ${color("common.white")} 100%);
            ${xScreen("lte", "md")} {
                background: linear-gradient(90deg, ${color("common.white", 0)} 0%, ${color("common.white")} 100%);
                right: 0;
                left: unset;
            }
        }
    `,
};

productImageCarouselStyles.videoStyles = {
    container: {
        css: css`
            padding-bottom: 0;
            .yt-lite {
                width: 64px;
                height: 24px;
            }
            .yt-lite > .lty-playbtn {
                width: 24px;
                height: 24px;
            }
            .yt-lite:hover > .lty-playbtn {
                width: 24px;
                height: 24px;
            }
        `,
    },
};
